import Layout from '@src/components/Layout';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Tab } from 'semantic-ui-react';
import { useDebounce } from 'use-lodash-debounce';
import i18n from '~/components/i18n';
import notify from '~/components/Notify';
import FarmerGroupQuery from '~/QueryHooks/FarmerGroupsQuery';
import { RouteTypes } from '~/routes/constants';
import { El } from '~/components/Layout/style';
import TableComponent from '~/components/TableComponent';
import { LIGHT_USER_ROLE_SLUG, tradingSlotModules } from '~/Constants';
import { useAuth } from '~/routes/components/AuthProvider';
import Dialog from '~/components/Dialog';
import FarmerGroupsService from '~/api/services/farmerGroups';
import { handleErrors } from '~/utils/helpers';
import CreateEditFarmerGroups from './CreateEditFarmerGroups';

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('ID'),
    key: 'uniqueId',
    sorted: 'desc'
  },
  {
    name: i18n.t('Name'),
    key: 'name',
    sorted: 'desc'
  },
  {
    name: i18n.t('Number of farmers'),
    key: 'farmersCount',
    sorted: 'desc'
  },
  {
    name: i18n.t('Trading amount (%/year)'),
    key: 'amountPercent',
    type: 'no-unit-number',
    sorted: 'desc'
  },
  {
    name: i18n.t('Actions')
  }
];

const getActions = () => [
  {
    popupMessage: i18n.t('Show more'),
    type: 'link',
    name: 'eye',
    size: 'large',
    link: RouteTypes.DAIRY_FACTORY_PROFILE_FARMER_GROUPS_SHOW
  },
  {
    popupMessage: i18n.t('Edit'),
    type: 'icon',
    name: 'pencil',
    size: 'large',
    actionType: 'edit'
  },
  {
    popupMessage: i18n.t('Deactivate farmer group'),
    type: 'icon',
    name: 'close',
    size: 'large',
    actionType: 'deactivate',
    color: 'red'
  },
  {
    popupMessage: i18n.t('Activate farmer group'),
    type: 'icon',
    name: 'checkmark',
    size: 'large',
    color: 'green',
    actionType: 'unblock',
    display: [0]
  },
  {
    popupMessage: i18n.t('Delete'),
    type: 'icon',
    name: 'trash',
    size: 'large',
    actionType: 'delete',
    verifiedAction: 0,
    verifiedActionKey: 'farmersCount'
  }
]

const getActionsForLightUser = () => [
  {
    popupMessage: i18n.t('Show more'),
    type: 'link',
    name: 'eye',
    size: 'large',
    link: RouteTypes.DAIRY_FACTORY_PROFILE_FARMER_GROUPS_SHOW
  }
]

const FarmerGroups: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { systemData: { logged: { roleSlug }, modules } } = useAuth()

  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 30,
    search: '',
    status: 1,
    column: '',
    sort: ''
  });
  const [search, setSearch] = useState('');
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [isBusy, setIsBusy] = useState(false)
  const debouncedSearchValue = useDebounce(search, 300);
  const [deactivateGroup, setDeactivateGroup] = useState(null)
  const [deleteFarmerGroupData, setDeleteFarmerGroupData] = useState(null)
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())
  const [loadingModalAction, setLoadingModalAction] = useState(false)
  const [drawer, setDrawerState] = useState({
    isVisible: false,
    id: null
  });

  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG
  const hasAnyTradingSlot = Boolean(modules.find(module => !!tradingSlotModules.find(tradingSlotSlug => tradingSlotSlug === module.slug)))

  const {
    isLoading: isBusyData,
    data: farmerGroupsData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = FarmerGroupQuery.getFarmerGroupsList({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  useEffect(() => {
    setTableColumnHeaders(getDefaultTableColumnHeaders())
  }, [t])

  useEffect(() => {
    if (dataFiltered) {
      fetchFilteredData();
    }
  }, [dataFiltered]);

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 });
  }, [debouncedSearchValue]);

  function renderSearch() {
    return (
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
    );
  }

  function handleClickAction(row, type) {
    switch (type) {
      case 'edit':
        return setDrawerState({
          id: row.id,
          isVisible: true
        })
      case 'deactivate':
        setDeactivateGroup(row)
        return
      case 'unblock':
        setDeactivateGroup(row)
        return
      case 'delete':
        return setDeleteFarmerGroupData(row)
      default:
        return
    }
  }

  function renderContent() {
    const actions = getActions()
    const filteredTableColumns = !hasAnyTradingSlot ? tableColumnHeaders.filter(column => column.key !== 'amountPercent') : tableColumnHeaders
    return <TableComponent
      actions={lightUserLogged ? getActionsForLightUser() : dataFiltered.status === 1 ? actions : actions.filter(action => action.actionType !== 'deactivate')}
      isFetchingData={isFetchingData}
      animated
      tableColumnHeaders={filteredTableColumns}
      dataFiltered={dataFiltered}
      handleClickAction={handleClickAction}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      rows={rows}
      total={filtered}
    />
  }

  function handleTabChange(activeIndex) {
    if (!isFetchingData) {
      setActiveTabIndex(activeIndex)
      setDataFiltered({
        ...dataFiltered,
        status: activeIndex === 0 ? 1 : 0,
        page: 0
      });
    }
  }

  function renderPanes() {
    const panes = [
      {
        menuItem: t('Active farmer groups'),
        render: () => renderContent()
      },
      {
        menuItem: t('Inactive farmer groups'),
        render: () => renderContent()
      }
    ];

    return (
      <Tab
        menu={{ secondary: true, pointing: true }}
        panes={panes}
        activeIndex={activeTabIndex}
        onTabChange={(_, data) => handleTabChange(data.activeIndex)}
      />
    );
  }

  function renderActions() {
    return !lightUserLogged && (
      <>
        <El.PageHeaderActionButton
          size='large'
          onClick={() => {
            setDrawerState({
              id: 'create',
              isVisible: true
            })
          }}
        >
          {t('Create')}
        </El.PageHeaderActionButton>
      </>
    );
  }

  async function handleDeactivateGroup() {
    const { id, status } = deactivateGroup
    setIsBusy(true);
    try {
      if (status === 1) {
        await FarmerGroupsService.blockFarmerGroup(id)
      } else if (status === 0) {
        await FarmerGroupsService.unblockFarmerGroup(id)
      }
      setDeactivateGroup(null)
      fetchFilteredData()
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderModal() {
    const action = deactivateGroup?.status === 1 ? t('Deactivate') : t('Activate')
    const name = deactivateGroup?.name
    return <Dialog
      isDialogOpen={Boolean(deactivateGroup)}
      textClose={t('Close')}
      handleClose={() => setDeactivateGroup(null)}
      textSubmit={action}
      handleSubmit={() => handleDeactivateGroup()}
      textContent={t('Do you want to {{action}} {{name}}?', { action: action.toLocaleLowerCase(), name })}
      textHeader={t('{{action}} {{name}}', { action, name })}
      type={'abort'}
    />
  }

  async function handleDeleteFarmerGroup() {
    setLoadingModalAction(true)
    try {
      await FarmerGroupsService.deleteFarmerGroup(deleteFarmerGroupData?.id)
      setDeleteFarmerGroupData(null)
      fetchFilteredData()
      notify(t('Successfully deleted farmer group'), 'success')
    } catch (error) {
      handleErrors(error)
    }
    setLoadingModalAction(false)
  }

  function renderDeleteFarmerModal() {
    const name = deleteFarmerGroupData?.companyName
    return <Dialog
      isDialogOpen={Boolean(deleteFarmerGroupData)}
      textHeader={t('Delete farmer group {{name}}', { name })}
      textClose={t('Close')}
      handleClose={() => setDeleteFarmerGroupData(null)}
      textContent={t('Do you want to delete farmer group {{name}}?', { name })}
      textSubmit={t('Delete')}
      type={'abort'}
      isLoading={loadingModalAction}
      disableSubmit={loadingModalAction}
      handleSubmit={() => handleDeleteFarmerGroup()}
    />
  }

  const { filtered, rows } = farmerGroupsData ? farmerGroupsData : { filtered: 0, rows: [] };
  const loading = isBusyData || isBusy;

  return (
    <Layout
      isBusy={loading}
      useSpinner
      childContainerSize={'300px'}
      fontSizeHeader={'18px'}
      filters={renderSearch()}
      actions={renderActions()}
      title={t('Farmer Groups')}
    >
      {deleteFarmerGroupData && renderDeleteFarmerModal()}
      {farmerGroupsData && renderPanes()}
      {renderModal()}
      {!lightUserLogged && <CreateEditFarmerGroups
        {...drawer}
        handleHiddenDrawer={(loadNew) => {
          setDrawerState({
            isVisible: false,
            id: null
          })
          if(loadNew) {
            fetchFilteredData()
          }
        }}
      />}
    </Layout>
  );
};

export default FarmerGroups;
